import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Upload, Loader2, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useCreateCategory } from "@/hooks/useManageServices";

// ─── Schema ──────────────────────────────────────────────────────────────────

const categorySchema = z.object({
  name: z.string().min(1, "Category name is required"),
});

type CategoryFormValues = z.infer<typeof categorySchema>;

// ─── Component ────────────────────────────────────────────────────────────────

interface AddCategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddCategoryDialog = ({
  open,
  onOpenChange,
}: AddCategoryDialogProps) => {
  const { toast } = useToast();
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconFileName, setIconFileName] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createCategoryMutation = useCreateCategory();

  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: { name: "" },
  });

  const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1048576) {
      toast({
        title: "Icon too large",
        description: "Maximum icon size is 1 MB.",
        variant: "destructive",
      });
      return;
    }
    setIconFile(file);
    setIconFileName(file.name);
  };

  const onSubmit = async (values: CategoryFormValues) => {
    if (!iconFile) {
      toast({
        title: "Icon required",
        description: "Please select a category icon.",
        variant: "destructive",
      });
      return;
    }

    const fd = new FormData();
    fd.append("name", values.name);
    fd.append("priority", "3");
    fd.append("icon", iconFile, iconFile.name);

    try {
      await createCategoryMutation.mutateAsync(fd);
      toast({ title: "Category added successfully." });
      form.reset();
      setIconFile(null);
      setIconFileName("");
      onOpenChange(false);
    } catch {
      toast({
        title: "Something went wrong",
        description: "Could not create category. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDiscard = () => {
    form.reset();
    setIconFile(null);
    setIconFileName("");
    onOpenChange(false);
  };

  const isPending = createCategoryMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Standard dialog template from DESIGN_SYSTEM_OVERVIEW.md / ui-playbook */}
      <DialogContent className="sm:max-w-[480px] max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0 bg-card border-border">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b border-border shrink-0">
          <DialogTitle className="text-foreground flex items-center gap-2">
            <div className="p-1.5 bg-muted rounded-lg">
              <Layers className="h-4 w-4 text-primary" />
            </div>
            Add New Category
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Create a new category to manage services
          </p>
        </DialogHeader>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto p-6">
          <Form {...form}>
            <form
              id="add-category-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
            >
              {/* Category Name */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Category Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="category-name-input"
                        placeholder="Eg. Yoga, Therapy, Doctor, Nutrition…"
                        className="bg-background border-border text-foreground"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Icon Upload */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Category Icon
                </p>
                <Button
                  id="upload-category-icon-btn"
                  type="button"
                  variant="outline"
                  className={cn(
                    "w-full border-border border-dashed",
                    iconFileName ? "text-foreground" : "text-muted-foreground"
                  )}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4" />
                  {iconFileName || "Upload Icon (PNG, JPG, SVG — max 1 MB)"}
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/png,image/jpeg,image/svg+xml"
                  className="hidden"
                  onChange={handleIconChange}
                />
              </div>
            </form>
          </Form>
        </div>

        {/* Footer — standard: p-6 pt-4 border-t */}
        <DialogFooter className="p-6 pt-4 border-t border-border shrink-0 gap-2">
          <Button
            id="discard-category-btn"
            type="button"
            variant="outline"
            className="border-border text-foreground"
            onClick={handleDiscard}
            disabled={isPending}
          >
            Discard
          </Button>
          <Button
            id="save-category-btn"
            type="submit"
            form="add-category-form"
            className="min-w-20"
            disabled={isPending}
          >
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
